// Email service for sending submission confirmations
import { IDSubmission } from '../types';
import { supabase } from '../lib/supabase';

// Enhanced HTML email template with interactive elements
const getConfirmationEmailTemplate = (data: {
  submissionId: string;
  fullName: string;
  documentTypes: string;
  paymentAmount: number;
  submissionDate: string;
  trackingUrl: string;
}): string => {
  return `<!DOCTYPE html>
<html>
<head>
    <meta charset="utf-8">
    <meta name="viewport" content="width=device-width, initial-scale=1.0">
    <title>Submission Received - IDsRewards</title>
    <style>
        .tracking-btn {
            background: linear-gradient(135deg, #10b981 0%, #059669 100%);
            color: white;
            padding: 12px 24px;
            border-radius: 8px;
            text-decoration: none;
            font-size: 14px;
            font-weight: 600;
            display: inline-block;
            margin: 16px 0;
        }
        .tracking-btn:hover {
            background: linear-gradient(135deg, #059669 0%, #047857 100%);
        }
        .submission-id {
            font-family: 'Courier New', monospace;
            background: #f1f5f9;
            padding: 4px 8px;
            border-radius: 4px;
            font-weight: bold;
            color: #1e293b;
            font-size: 13px;
        }
    </style>
</head>
<body>
    <!-- IDsRewards Submission Confirmation template -->
    <table width="100%" cellpadding="0" cellspacing="0" role="presentation" style="font-family: Arial, Helvetica, sans-serif; background:#f6f8fb; padding:24px 0;">
      <tr>
        <td align="center">
          <table width="600" cellpadding="0" cellspacing="0" role="presentation" style="background:#ffffff; border-radius:12px; overflow:hidden; box-shadow:0 8px 30px rgba(20,20,50,0.06);">
            <!-- Header -->
            <tr>
              <td style="background:linear-gradient(135deg,#667eea 0%,#764ba2 100%); padding:28px 24px; text-align:center;">
                <h1 style="margin:0; color:#ffffff; font-size:20px; font-weight:700; letter-spacing:0.2px;">IDsRewards — Submission Received</h1>
                <p style="margin:6px 0 0 0; color:rgba(255,255,255,0.9); font-size:13px;">Your documents are being reviewed</p>
              </td>
            </tr>

            <!-- Body -->
            <tr>
              <td style="padding:22px 28px 18px 28px; color:#333333;">
                <p style="margin:0 0 12px 0; font-size:15px;">
                  Hello <strong>${data.fullName}</strong>,
                </p>

                <p style="margin:0 0 16px 0; color:#444; font-size:14px;">
                  Thank you for submitting your documents to IDsRewards. We have successfully received your submission and our team will review it shortly.
                </p>

                <!-- Submission Details -->
                <div style="margin:18px 0; padding:20px; background:#f7f9ff; border-radius:10px;">
                  <h3 style="margin:0 0 12px 0; color:#667eea; font-size:16px; font-weight:600;">Submission Details</h3>
                  
                  <!-- Prominent Submission ID Box -->
                  <div style="margin:0 0 16px 0; padding:12px; background:#e0f2fe; border-radius:8px; border:2px dashed #0284c7; text-align:center;">
                    <p style="margin:0 0 4px 0; color:#0c4a6e; font-size:12px; font-weight:600; text-transform:uppercase;">Your Submission ID</p>
                    <div style="font-family: 'Courier New', monospace; font-size:16px; font-weight:bold; color:#0c4a6e; letter-spacing:1px; margin:4px 0; padding:8px; background:white; border-radius:4px; border:1px solid #0284c7;">
                      ${data.submissionId}
                    </div>
                    <p style="margin:4px 0 0 0; color:#0369a1; font-size:11px;">💡 Save this ID to track your submission</p>
                  </div>
                  
                  <p style="margin:0 0 8px 0; color:#444; font-size:14px;">
                    <strong>Document Types:</strong> ${data.documentTypes}
                  </p>
                  <p style="margin:0 0 16px 0; color:#444; font-size:14px;">
                    <strong>Submitted:</strong> ${data.submissionDate}
                  </p>
                  
                  <!-- Track Submission Button -->
                  <div style="text-align: center; margin-top: 20px;">
                    <a href="${data.trackingUrl}" class="tracking-btn" style="color: white; text-decoration: none;">🔍 Track Your Submission</a>
                  </div>
                </div>

                <p style="margin:16px 0; color:#444; font-size:14px;">
                  <strong>What happens next?</strong>
                </p>
                
                <ol style="margin:0 0 16px 20px; padding:0; color:#444; font-size:14px;">
                  <li style="margin-bottom:8px;">Our team will verify your documents within 24-48 hours</li>
                  <li style="margin-bottom:8px;">If approved, you'll receive an email with payment details</li>
                  <li style="margin-bottom:8px;">Payment will be processed to your provided bank account</li>
                  <li>You'll receive a payment confirmation once completed</li>
                </ol>

                <hr style="border:none; border-top:1px solid #efefef; margin:18px 0;" />

                <p style="margin:0; color:#666; font-size:13px;">
                  You can check your submission status anytime by clicking the "Track Your Submission" button above or visiting our portal with your submission ID. If you have any questions, please contact our support team.
                </p>
              </td>
            </tr>

            <!-- Footer -->
            <tr>
              <td style="padding:18px 28px; background:#fafbfe; color:#7b7f92; font-size:12px; text-align:center;">
                <div style="margin-bottom:6px;">Need help? Contact support via the app.</div>
                <div>© IDsRewards.com — IDsRewards</div>
              </td>
            </tr>
          </table>
        </td>
      </tr>
    </table>
</body>
</html>`;
};

export const sendSubmissionConfirmationEmail = async (
  submission: IDSubmission
): Promise<boolean> => {
  try {
    // Extract email from submission
    const recipientEmail = submission.personalInfo?.email;
    if (!recipientEmail) {
      console.error('No email found in submission');
      return false;
    }

    // Sending confirmation email...

  // Use only environment-configured site URL for links. Abort if not set to avoid sending admin URLs.
  const envSite = process.env.REACT_APP_SITE_URL;
  if (!envSite) {
    console.error('REACT_APP_SITE_URL is not set; aborting sending confirmation email to avoid incorrect tracking links.');
    return false;
  }
  const siteBase = envSite.replace(/\/$/, '');
  const trackingUrl = `${siteBase}/track/${submission.id}`;
    
    // Generate the email template locally
    const templateData = {
      submissionId: submission.id,
      fullName: submission.personalInfo?.fullName || 'Valued Customer',
      documentTypes: submission.documents?.map(doc => doc.type).join(', ') || 'Documents',
      paymentAmount: submission.payment_amount || 0,
      submissionDate: new Date(submission.created_at).toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'long',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit'
      }),
      trackingUrl
    };

    const htmlContent = getConfirmationEmailTemplate(templateData);
    const subject = `Submission Received - ID: ${submission.id.slice(0, 8)}`;

    // Use Supabase Edge Function to send the email
    try {
      const emailData = {
        recipientEmail: recipientEmail,
        subject: subject,
        htmlContent: htmlContent
      };

      const { error } = await supabase.functions.invoke('send-email', {
        body: emailData
      });

      if (!error) {
        // Email sent successfully via Supabase Edge Function
        return true;
      } else {
        console.warn('⚠️ Edge Function not available, trying fallback method...');
        throw new Error('Edge function failed');
      }
    } catch (edgeFunctionError) {
      // Fallback: Use a simple approach that won't fail
      // Using fallback email method (Magic Link)...
      
      // Send a "magic link" email which will contain our custom message
      // This uses Supabase's built-in email system
      const { error } = await supabase.auth.signInWithOtp({
        email: recipientEmail,
        options: {
          data: {
            submission_id: submission.id,
            full_name: submission.personalInfo?.fullName,
            custom_type: 'submission_confirmation'
          }
        }
      });

      if (error) {
        console.error('❌ Fallback email method failed:', error);
        return false;
      }

      // Fallback email sent successfully (magic link with custom data)
      return true;
    }

  } catch (error) {
    console.error('❌ Error sending confirmation email:', error);
    return false;
  }
};

// Email service for status updates (approval, rejection, payment)
export const sendStatusUpdateEmail = async (
  submission: IDSubmission,
  newStatus: 'approved_pending_payment' | 'rejected' | 'paid',
  additionalInfo?: {
    rejectionReason?: string;
    paymentDetails?: any;
  }
): Promise<boolean> => {
  try {
    const recipientEmail = submission.personalInfo?.email;
    if (!recipientEmail) {
      console.error('No email found in submission');
      return false;
    }

    let subject = '';
    let statusMessage = '';
    let statusColor = '#667eea';

    switch (newStatus) {
      case 'approved_pending_payment':
        subject = `🎉 Great News! Your Documents Are Approved - Payment Processing`;
        statusMessage = 'Congratulations! Your document submission has passed our verification process. Our finance team is now preparing your payment and will transfer the funds to your bank account shortly.';
        statusColor = '#10b981';
        break;
      case 'rejected':
        subject = `📋 Action Required - Document Review Needed`;
        statusMessage = `We've carefully reviewed your submission, but we need some adjustments before we can proceed with payment. Don't worry - this happens sometimes and we're here to help you get it right!`;
        statusColor = '#ef4444';
        break;
      case 'paid':
        subject = `💰 Payment Sent Successfully - Funds On The Way!`;
        statusMessage = `Excellent news! We've successfully transferred your payment to your bank account. Depending on your bank's processing time, the funds should appear in your account within 1-3 business days.`;
        statusColor = '#10b981';
        break;
    }

    const templateData = {
      submissionId: submission.id,
      fullName: submission.personalInfo?.fullName || 'Valued Customer',
      statusMessage,
      rejectionReason: additionalInfo?.rejectionReason || '',
      paymentAmount: submission.payment_amount || 0,
      statusColor,
  // Use only REACT_APP_SITE_URL for status email links; abort earlier if not present
  trackingUrl: `${process.env.REACT_APP_SITE_URL!.replace(/\/$/, '')}/track/${submission.id}`
    };

    // Enhanced status update email template with same styling
    const statusEmailTemplate = `<!DOCTYPE html>
<html>
<head>
    <meta charset="utf-8">
    <meta name="viewport" content="width=device-width, initial-scale=1.0">
    <title>${subject} - IDsRewards</title>
    <style>
        .tracking-btn {
            background: linear-gradient(135deg, #10b981 0%, #059669 100%);
            color: white;
            padding: 12px 24px;
            border-radius: 8px;
            text-decoration: none;
            font-size: 14px;
            font-weight: 600;
            display: inline-block;
            margin: 16px 0;
        }
        .tracking-btn:hover {
            background: linear-gradient(135deg, #059669 0%, #047857 100%);
        }
        .submission-id {
            font-family: 'Courier New', monospace;
            background: #f1f5f9;
            padding: 4px 8px;
            border-radius: 4px;
            font-weight: bold;
            color: #1e293b;
            font-size: 13px;
        }
    </style>
</head>
<body>
    <table width="100%" cellpadding="0" cellspacing="0" role="presentation" style="font-family: Arial, Helvetica, sans-serif; background:#f6f8fb; padding:24px 0;">
      <tr>
        <td align="center">
          <table width="600" cellpadding="0" cellspacing="0" role="presentation" style="background:#ffffff; border-radius:12px; overflow:hidden; box-shadow:0 8px 30px rgba(20,20,50,0.06);">
            <tr>
              <td style="background:linear-gradient(135deg,#667eea 0%,#764ba2 100%); padding:28px 24px; text-align:center;">
                <h1 style="margin:0; color:#ffffff; font-size:20px; font-weight:700; letter-spacing:0.2px;">IDsRewards — Status Update</h1>
                <p style="margin:6px 0 0 0; color:rgba(255,255,255,0.9); font-size:13px;">Submission ID: <span class="submission-id" style="color: white; background: rgba(255,255,255,0.2);">${templateData.submissionId.slice(0, 8)}</span></p>
              </td>
            </tr>
            <tr>
              <td style="padding:22px 28px 18px 28px; color:#333333;">
                <p style="margin:0 0 12px 0; font-size:15px;">
                  Hello <strong>${templateData.fullName}</strong>,
                </p>
                <p style="margin:0 0 16px 0; color:#444; font-size:14px;">
                  ${templateData.statusMessage}
                </p>
                
                <!-- Prominent Submission ID Box -->
                <div style="margin:0 0 16px 0; padding:12px; background:#e0f2fe; border-radius:8px; border:2px dashed #0284c7; text-align:center;">
                  <p style="margin:0 0 4px 0; color:#0c4a6e; font-size:12px; font-weight:600; text-transform:uppercase;">Your Submission ID</p>
                  <div style="font-family: 'Courier New', monospace; font-size:16px; font-weight:bold; color:#0c4a6e; letter-spacing:1px; margin:4px 0; padding:8px; background:white; border-radius:4px; border:1px solid #0284c7;">
                    ${templateData.submissionId}
                  </div>
                  <p style="margin:4px 0 0 0; color:#0369a1; font-size:11px;">💡 Use this ID to track your submission</p>
                </div>
                
                <div style="margin:18px 0; padding:20px; background:#f7f9ff; border-radius:10px; border-left:4px solid ${templateData.statusColor};">
                  <p style="margin:0 0 12px 0; color:#444; font-size:14px;">
                    <strong>Current Status:</strong> <span style="color:${templateData.statusColor}; font-weight:600;">${newStatus.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase())}</span>
                  </p>
                  
                  ${templateData.paymentAmount > 0 ? `<p style="margin:0 0 12px 0; color:#444; font-size:14px;"><strong>💰 Payment Amount:</strong> <span style="color:#10b981; font-weight:600; font-size:16px;">$${templateData.paymentAmount}</span></p>` : ''}
                  
                  ${newStatus === 'approved_pending_payment' ? `
                    <div style="margin:12px 0; padding:12px; background:#ecfdf5; border-radius:6px; border-left:3px solid #10b981;">
                      <p style="margin:0; color:#065f46; font-size:13px;"><strong>⏰ What's Next:</strong></p>
                      <p style="margin:4px 0 0 0; color:#065f46; font-size:13px;">• Our finance team is processing your payment</p>
                      <p style="margin:2px 0 0 0; color:#065f46; font-size:13px;">• Funds will be transferred to your bank account within 24-48 hours</p>
                      <p style="margin:2px 0 0 0; color:#065f46; font-size:13px;">• You'll receive a confirmation email once payment is complete</p>
                    </div>
                  ` : ''}
                  
                  ${newStatus === 'paid' ? `
                    <div style="margin:12px 0; padding:12px; background:#ecfdf5; border-radius:6px; border-left:3px solid #10b981;">
                      <p style="margin:0; color:#065f46; font-size:13px;"><strong>🏦 Payment Details:</strong></p>
                      <p style="margin:4px 0 0 0; color:#065f46; font-size:13px;">• Payment has been successfully sent to your bank account</p>
                      <p style="margin:2px 0 0 0; color:#065f46; font-size:13px;">• Funds typically arrive within 1-3 business days</p>
                      <p style="margin:2px 0 0 0; color:#065f46; font-size:13px;">• Check your bank statement for: "IDsRewards Payment"</p>
                      <p style="margin:6px 0 0 0; color:#065f46; font-size:12px; font-style:italic;">💡 If you don't see the funds after 3 business days, please contact your bank first as processing times vary.</p>
                    </div>
                  ` : ''}
                  
                  ${templateData.rejectionReason ? `
                    <div style="margin:12px 0; padding:12px; background:#fef2f2; border-radius:6px; border-left:3px solid #ef4444;">
                      <p style="margin:0 0 8px 0; color:#991b1b; font-size:13px;"><strong>📝 Review Notes from Our Team:</strong></p>
                      <p style="margin:0 0 12px 0; color:#7f1d1d; font-size:13px; font-style:italic;">"${templateData.rejectionReason}"</p>
                      <div style="margin:8px 0 0 0; padding:8px; background:#ffffff; border-radius:4px;">
                        <p style="margin:0; color:#991b1b; font-size:12px;"><strong>🔄 Next Steps:</strong></p>
                        <p style="margin:4px 0 0 0; color:#7f1d1d; font-size:12px;">1. Review the feedback above carefully</p>
                        <p style="margin:2px 0 0 0; color:#7f1d1d; font-size:12px;">2. Take new photos following our guidelines</p>
                        <p style="margin:2px 0 0 0; color:#7f1d1d; font-size:12px;">3. Submit a new application with improved documents</p>
                        <p style="margin:6px 0 0 0; color:#059669; font-size:12px; font-weight:600;">💪 Don't give up! Most users succeed on their second try.</p>
                      </div>
                    </div>
                  ` : ''}
                  
                  <!-- Track Submission Button -->
                  <div style="text-align: center; margin-top: 20px;">
                    <a href="${templateData.trackingUrl}" class="tracking-btn" style="color: white; text-decoration: none;">${newStatus === 'rejected' ? '🔄 Submit New Application' : newStatus === 'paid' ? '� View Payment History' : '🔍 Track Submission Status'}</a>
                  </div>
                </div>
                
                ${newStatus === 'paid' ? `
                  <div style="margin:16px 0; padding:16px; background:linear-gradient(135deg, #667eea 0%, #764ba2 100%); border-radius:8px; text-align:center;">
                    <p style="margin:0 0 8px 0; color:white; font-size:16px; font-weight:600;">🎉 Thank you for choosing IDsRewards!</p>
                    <p style="margin:0; color:rgba(255,255,255,0.9); font-size:13px;">Want to earn more? Submit additional documents for extra income!</p>
                  </div>
                ` : ''}
                
                <p style="margin:0; color:#666; font-size:13px;">
                  ${newStatus === 'rejected' ? 'Need help improving your submission? Our support team is ready to assist you with photo guidelines and tips.' : newStatus === 'paid' ? 'We appreciate your business! Consider sharing IDsRewards with friends and family.' : 'We appreciate your patience during the review process. Your payment will be processed as quickly as possible.'}
                </p>
                </p>
              </td>
            </tr>
            <tr>
              <td style="padding:18px 28px; background:#fafbfe; color:#7b7f92; font-size:12px; text-align:center;">
                <div style="margin-bottom:6px;">Need help? Contact support via the app.</div>
                <div>© IDsRewards.com — IDsRewards</div>
              </td>
            </tr>
          </table>
        </td>
      </tr>
    </table>
</body>
</html>`;

    // Use Supabase Edge Function for status updates too
    try {
      const emailData = {
        recipientEmail: recipientEmail,
        subject: subject,
        htmlContent: statusEmailTemplate
      };

      const { error } = await supabase.functions.invoke('send-email', {
        body: emailData
      });

      if (!error) {
        // Status update email sent successfully via Supabase Edge Function
        return true;
      } else {
        console.error('❌ Failed to send status update email via Edge Function:', error);
        return false;
      }
    } catch (error) {
      console.error('❌ Error sending status update email:', error);
      return false;
    }
  } catch (error) {
    console.error('❌ Error sending status update email:', error);
    return false;
  }
};